import { useState, useEffect } from 'react';
import {fetchStocks, uploadStocks, updateStock, addStock} from '../services/stockService';
import { flattenProductData, preprocessRestockRows, getActiveFilters } from '../utils/helper';
import API from "../../../../network/dashboardAPI";

export const useStockManagement = (props) => {
    const admin = localStorage.getItem("admin");
    const [stocks, setStocks] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [uploadErrorMessage, setUploadErrorMessage] = useState("");
    const [inputPage, setInputPage] = useState('');
    const [showFilter, setShowFilter] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [filtersDirty, setFiltersDirty] = useState(false);
    const limit = 10;

    // Sort
    const [orderField, setOrderField] = useState("item_id");
    const [orderType, setOrderType] = useState("DESC");
    const orderTypeOpts = ["DESC", "ASC"];

    // Filters
    const [filters, setFilters] = useState({
        supplier_id: '',
        supplier: null,
        product_code: '',
        supplier_product_name: '',
        supplier_product_number: '',
        brand: '',
        warehouse_id: '',
        warehouse_name_set: null,
        location_name: '',
        available_quantity: '',
        is_ds_warehouse: null,
    });

    const [exportFilters, setExportFilters] = useState({
        warehouse_id: '',
        warehouse_name_set: null,
        available_quantity: '',
        is_ds_warehouse: null,
        supplier_id: '',
        supplier: null,
    });

    // Edited stocks tracking
    const [editedStocks, setEditedStocks] = useState({});

    // Warehouse locations cache
    const [warehouseLocationsMap, setWarehouseLocationsMap] = useState({});

    // Upload state
    const [showUploadStock, setShowUploadStock] = useState(false);
    const [restockFile, setReStockFile] = useState(null);
    const [restockUpload, setRestockUpload] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isOpenModalExportReport, setIsOpenModalExportReport] = useState(false);

    // Fetch stocks when component mounts or page changes
    useEffect(() => {
        const getStocks = async () => {
            setLoading(true);
            setError(null);
            try {
                const filtersToSend = getActiveFilters(filters);
                const data = await fetchStocks(page, limit, orderField, orderType, filtersToSend);
                setTotalPages(data.total_pages);
                if (data && data.list) {
                    const flattenData = flattenProductData(data.list, orderField, orderType);
                    setStocks(flattenData);
                    
                    // Preload warehouse locations for each stock item
                    const uniqueWarehouseIds = [...new Set(flattenData.map(stock => stock.warehouse_id))];
                    uniqueWarehouseIds.forEach(warehouseId => {
                        if (warehouseId) {
                            getWarehouseLocations(warehouseId);
                        }
                    });
                }
            } catch (err) {
                console.error('Error fetching stocks:', err);
                setError('Failed to fetch stocks. Please try again.');
            } finally {
                setLoading(false);
            }
        };
        getStocks();
    }, [page, orderField, orderType, filtersDirty]);

    const getWarehouseLocations = async (warehouse_id, forceRefresh = false) => {
        if (!warehouse_id) {
            return [];
        }
        
        // Return cached data unless force refresh is requested
        if (!forceRefresh && warehouseLocationsMap[warehouse_id]) {
            return warehouseLocationsMap[warehouse_id];
        }
        
        try {
            const response = await API.getWarehouseLocationsByWarehouseID({
                warehouse_id: warehouse_id
            });
            const data = await response.json();
            
            if (data.success) {
                const warehouseLocationRecords = (data.list || []).map(item => ({
                    value: item.name,
                    label: item.name,
                }));
                
                setWarehouseLocationsMap(prev => ({
                    ...prev,
                    [warehouse_id]: warehouseLocationRecords
                }));
                
                return warehouseLocationRecords;
            }
        } catch (error) {
            console.error('Error fetching warehouse locations:', error);
        }
        return [];
    };

    // Handle sort
    const handleSortOrder = (field) => {
        if (field === orderField) {
            setOrderType(orderTypeOpts.find(x => x !== orderType));
        } else {
            setOrderType("ASC");
            setOrderField(field);
        }
    };

    // Handle pagination
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages && newPage !== page) {
            setPage(newPage);
        }
    };

    // Filter handlers
    const handleFilterChange = (field, value, autoApply = false) => {
        setFilters(prevFilters => {
            if (field === 'warehouse_name') {
                return {
                    ...prevFilters,
                    warehouse_id: value ? value.id : null,
                    warehouse_name_set: value ?? null,
                };
            } else if (field === 'supplier') {
                return {
                    ...prevFilters,
                    supplier_id: value ? value.store_users[0].id : null,
                    supplier: value ?? null,
                };
            } else {
                return {
                    ...prevFilters,
                    [field]: value,
                };
            }
        });

		// Auto-apply filters for select components
		if (autoApply) {
			setPage(1);
			setTimeout(() => {
				setFiltersDirty(prev => !prev);
			}, 0);
		}
    };

    const handleExportFilterChange = (field, value) => {
        setExportFilters(prevFilters => {
            if (field === 'warehouse_name') {
                return {
                    ...prevFilters,
                    warehouse_id: value ? value.id : null,
                    warehouse_name_set: value ?? null,
                };
            } else if (field === 'supplier') {
                return {
                    ...prevFilters,
                    supplier_id: value ? value.store_users[0].id : null,
                    supplier: value ?? null,
                };
            } else {
                return {
                    ...prevFilters,
                    [field]: value,
                };
            }
        });
    };

    const applyFilters = () => {
        setPage(1);
        setFiltersDirty(!filtersDirty);
    };

	// Handle keyboard events
	const handleKeyPress = (e) => {
		if (e.key === 'Enter') {
			applyFilters();
		}
	};

    const resetFilters = () => {
        setPage(1);
        setFilters({
            supplier_id: '',
            supplier: null,
            product_code: '',
            supplier_product_name: '',
            supplier_product_number: '',
            brand: '',
            warehouse_name: '',
            location_name: '',
            available_quantity: '',
            is_ds_warehouse: null,
        });
        setFiltersDirty(!filtersDirty);
    };

    // Stock editing handlers
    const handleEditStockChange = (stock_id, field, value) => {
        setEditedStocks(prev => ({
            ...prev,
            [stock_id]: {...prev[stock_id], [field]: value}
        }));
    };

    const handleWarehouseLocationStockChange = async (stock_id, warehouse_id, value) => {
        setEditedStocks(prev => ({
            ...prev,
            [stock_id]: {...prev[stock_id], location_name: value} 
        }));
        // Extract the actual string value for the API call
        const locationValue = value?.value || value;
        await handleLocationUpdate(stock_id, warehouse_id, locationValue);
    };

    const handleQuantityBlur = async (stock_id) => {
        const updated = editedStocks[stock_id];
        if (!updated || updated.available_quantity === undefined) return;
        try {
            await updateStock(stock_id, updated.available_quantity);
        } catch (err) {
            console.error('Update failed:', err);
        }
    };

    const handleLocationUpdate = async (stock_id, warehouse_id, value) => {
        if (stock_id && warehouse_id && value === undefined) return;
        try {
            await updateStock(stock_id, null, warehouse_id, value);
            console.log("Location updated successfully");
        } catch (err) {
            console.error('Location update failed:', err);
        }
    };

    // Upload handlers
    const handleShowUploadStock = () => {
        setShowUploadStock(prev => !prev);
        setReStockFile(null);
        setUploadErrorMessage(null);
    };

    const handleChangeStockFile = (event) => {
        const files = event.target.files;
        if (files.length) {
            const file = files[0];
            const reader = new FileReader();
            reader.onload = (e) => {
                const { read, utils } = require('xlsx');
                const wb = read(e.target.result);
                const sheets = wb.SheetNames;
                if (sheets.length) {
                    const rows = utils.sheet_to_json(wb.Sheets[sheets[0]]);
                    setReStockFile(rows.slice(0, 50));
                    setRestockUpload(rows);
                }
            };
            reader.readAsArrayBuffer(file);
        }
    };

    const handleSubmitUploadStock = (e) => {
        setIsLoading(true);
        setUploadErrorMessage(null);
        e.preventDefault();
        if (restockUpload) {
            const processedRows = preprocessRestockRows(restockUpload, admin);
            uploadStocks(processedRows).then(response => {
                if (response.success) {
                    window.location.reload();
                } else {
                    console.error(response);
                    setUploadErrorMessage(response.error_message ?? response.error);
                }
            }).catch(error => {
                console.error(error);
            }).finally(() => {
                setIsLoading(false);
            });
        } else {
            alert("You haven't selected any file yet!");
            setIsLoading(false);
        }
    };

    // Export handlers
    const handleExportReport = () => {
        setIsOpenModalExportReport(true);
    };

    const handleSubmitExportReport = async () => {
        const xlsx = require('json-as-xlsx');
        const filtersToSend = getActiveFilters(exportFilters);
        fetchStocks(null, null, null, null, filtersToSend)
            .then(response => {
                if (response.list) {
                    const flattenData = flattenProductData(response.list, orderField, orderType);
                    let columns = [];
                    let content = [];
                    if (admin) {
                        columns = [
                            { label: 'ID', value: 'item_id' },
                            { label: 'supplier_product_name', value: 'supplier_product_name' },
                            { label: 'supplier_product_number', value: 'supplier_product_number' },
                            { label: 'warehouse_name', value: 'warehouse_name' },
                            { label: 'location_name', value: 'location_name' },
                            { label: 'available_quantity', value: 'available_quantity' },
                            { label: 'product_status', value: 'product_status' },
                            { label: 'is_ds_warehouse', value: 'is_ds_warehouse' },
                        ];
                        content = flattenData.map(stock => ({
                            item_id: stock.item_id,
                            supplier_product_name: stock.supplier_product_name,
                            supplier_product_number: stock.supplier_product_number,
                            warehouse_name: stock.warehouse_name,
                            location_name: stock.location_name,
                            available_quantity: stock.available_quantity,
                            product_status: stock.product_status ? 'Enabled' : 'Disabled',
                            is_ds_warehouse: stock.is_ds_warehouse ? 'Yes' : 'No',
                        }));
                    } else {
                        columns = [
                            { label: 'supplier_product_name', value: 'supplier_product_name' },
                            { label: 'supplier_product_number', value: 'supplier_product_number' },
                            { label: 'warehouse_name', value: 'warehouse_name' },
                            { label: 'location_name', value: 'location_name' },
                            { label: 'available_quantity', value: 'available_quantity' },
                        ];
                        content = flattenData.map(stock => ({
                            supplier_product_name: stock.supplier_product_name,
                            supplier_product_number: stock.supplier_product_number,
                            warehouse_name: stock.warehouse_name,
                            location_name: stock.location_name,
                            available_quantity: stock.available_quantity,
                        }));
                    }
                    const reportData = [
                        {
                            sheet: 'Stock Report',
                            columns: columns,
                            content: content,
                        },
                    ];
                    const settings = {
                        fileName: 'Stock_Report',
                        extraLength: 3,
                        writeMode: 'writeFile',
                        writeOptions: {},
                        RTL: false,
                    };
                    xlsx(reportData, settings);
                } else {
                    console.error(response);
                    alert("Failed to export report: " + (response.error_message || response.error));
                }
            })
            .catch(error => {
                console.error('Error exporting report:', error);
                alert("Error exporting report: " + error.message);
            }).finally(() => {
                setIsOpenModalExportReport(false);
            });
    };

    return {
        // State
        admin,
        stocks,
        page,
        totalPages,
        loading,
        error,
        uploadErrorMessage,
        inputPage,
        showFilter,
        isLoading,
        filters,
        exportFilters,
        filtersDirty,
        limit,
        orderField,
        orderType,
        editedStocks,
        warehouseLocationsMap,
        showUploadStock,
        restockFile,
        isOpenModalExportReport,
        
        // Setters
        setStocks,
        setTotalPages,
        setError,
        setInputPage,
        setShowFilter,
        setIsOpenModalExportReport,
        
        // Handlers
        handleSortOrder,
        handlePageChange,
        handleFilterChange,
        handleExportFilterChange,
        applyFilters,
        handleKeyPress,
        resetFilters,
        handleEditStockChange,
        handleWarehouseLocationStockChange,
        handleQuantityBlur,
        handleLocationUpdate,
        handleShowUploadStock,
        handleChangeStockFile,
        handleSubmitUploadStock,
        handleExportReport,
        handleSubmitExportReport,
        getWarehouseLocations,
    };
};
